import { DateTime } from 'luxon';
import { Reducer } from 'redux';
import { AppThunkAction } from '..';

import Apis from '../../apis';
import {
  LoginResponse,
  MenuItem,
  SchoolRank,
  Semester,
  SimpleSchool,
} from '../../model';
import { SemesterUtils } from '../../utils/semester';
import { listToObject } from '../../utils/transform';

type UserState = {
  name: string;
  mail?: string;
  currentSchool: string;
  currentRank: SchoolRank;
  schools: { [k: string]: SimpleSchool };
  menus: MenuItem[];
  semesters: { [k: string]: Semester };
  currentSemester: string;
  currentDate: string;
  exportFileType: number;
  nextOTPTime: DateTime;
};

export const NullUserState: UserState = Object.freeze({
  name: 'Unknown',
  currentSchool: 'Unknown',
  currentRank: SchoolRank.Primary,
  schools: {},
  menus: [],
  semesters: {},
  currentSemester: 'Unknown',
  currentDate: '',
  exportFileType: 0,
  nextOTPTime: DateTime.min(),
});

export interface AuthState {
  loading: boolean;
  isAuth?: boolean;
  user: UserState;
}

export enum UserActionType {
  SignIn = 'UserSignIn',
  SignOut = 'UserSignOut',
  KickOut = 'UserKickOut',
  Loading = 'Loading',
}

export interface UserSignIn {
  type: UserActionType.SignIn;
  user: UserState;
}

export interface UserSignOut {
  type:
    | UserActionType.SignOut
    | UserActionType.Loading
    | UserActionType.KickOut;
}

export type AuthActions = UserSignIn | UserSignOut;

function wrapGroupMEnuItem(menuItems: MenuItem[]): MenuItem[] {
  return [
    {
      icon: 'icon-navigation',
      id: 'root',
      type: 'group',
      title: '目錄',
      children: menuItems,
    },
  ];
}

const transfromUserResponse = (userResponse: LoginResponse): UserState => {
  const {
    user,
    schools,
    menus,
    currentSchool,
    currentRank,
    semesters,
    currentDate,
    exportFileType,
    nextOTPTime,
  } = userResponse;
  const currentSemester = SemesterUtils.stringify(
    semesters.find((s) => s.isNow)
  );
  return {
    name: user.name,
    mail: user.mail || undefined,
    currentSchool: currentSchool || '',
    currentRank,
    schools: listToObject(
      schools,
      (s) => s.id,
      (s) => s
    ),
    menus: wrapGroupMEnuItem(menus),
    semesters: listToObject(
      semesters,
      (s) => SemesterUtils.stringify(s),
      (s) => s
    ),
    currentSemester,
    currentDate: DateTime.fromISO(currentDate).toFormat('yyyy/MM/dd HH:mm:ss'),
    exportFileType: exportFileType ?? 0,
    nextOTPTime: nextOTPTime ?? DateTime.min(),
  };
};

const updateAuth =
  (userResponse: LoginResponse): AppThunkAction<AuthActions> =>
  async (dispatch, getState) => {
    dispatch({
      type: UserActionType.SignIn,
      user: transfromUserResponse(userResponse),
    });
  };

const refreshToken =
  (id?: string): AppThunkAction<AuthActions> =>
  async (dispatch, getState) => {
    dispatch({ type: UserActionType.Loading });
    try {
      const response = await Apis.refreshToken(id);

      return updateAuth(response)(dispatch, getState);
    } catch (e) {
      dispatch({ type: UserActionType.SignOut });
      throw e;
    }
  };

export const AuthDispatches = Object.freeze({
  signIn:
    (
      account: string,
      password: string,
      sync: boolean
    ): AppThunkAction<AuthActions> =>
    async (dispatch, getState) => {
      dispatch({ type: UserActionType.Loading });
      try {
        const response = await Apis.login(account, password, sync);

        return updateAuth(response)(dispatch, getState);
      } catch (e) {
        await Apis.logout().catch(() => {});
        dispatch({ type: UserActionType.SignOut });
        throw e;
      }
    },
  signOut: (): AppThunkAction<UserSignOut> => async (dispatch, getState) => {
    dispatch({ type: UserActionType.Loading });
    await Apis.logout().catch(() => {});
    dispatch({ type: UserActionType.SignOut });
  },
  checkAuth: refreshToken,
  changeSchool: refreshToken,
  updateAuth: updateAuth,
});

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const authReducer: Reducer<AuthState, AuthActions> = (
  state: AuthState | undefined,
  incomingAction: AuthActions
): AuthState => {
  if (state === undefined) {
    return { loading: false, user: NullUserState };
  }
  const action = incomingAction;
  switch (action.type) {
    case UserActionType.Loading:
      return {
        ...state,
        loading: true,
      };
    case UserActionType.SignIn:
      if (!action.user) {
        return { user: NullUserState, loading: false, isAuth: false };
      }
      return {
        loading: false,
        isAuth: true,
        user: action.user,
      };
    case UserActionType.SignOut:
    case UserActionType.KickOut:
      return { user: NullUserState, loading: false, isAuth: false };
    default:
      return state;
  }
};
